import { useCallback } from 'react'
import { graphql, useMutation, useRefetchableFragment } from 'react-relay'

import {
  type useProfilePhotos_profile$data,
  type useProfilePhotos_profile$key
} from '@/__generated__/useProfilePhotos_profile.graphql'
import { type useProfilePhotosCreatePhotoMutation } from '@/__generated__/useProfilePhotosCreatePhotoMutation.graphql'
import { type UseProfilePhotosQuery } from '@/__generated__/UseProfilePhotosQuery.graphql'

export type UseProfilePhotosEdge = NonEmpty<
  ElementOf<NonEmpty<useProfilePhotos_profile$data['profilePhotos']['edges']>>
>

export const useProfilePhotos = (profileRef: useProfilePhotos_profile$key | null | undefined) => {
  // noinspection GraphQLUnresolvedReference
  const [profileData, refetchProfilePhotos] = useRefetchableFragment<
    UseProfilePhotosQuery,
    useProfilePhotos_profile$key
  >(
    graphql`
      fragment useProfilePhotos_profile on Profile
      @argumentDefinitions(after: { type: "Cursor" }, first: { type: "Int", defaultValue: 10 })
      @refetchable(queryName: "UseProfilePhotosQuery") {
        profilePhotos: photos(after: $after, first: $first, filter: { type: { equalTo: PROFILE } })
          @connection(key: "useProfilePhotos_profile_profilePhotos", filters: ["filter"]) {
          __id
          edges {
            cursor
            node {
              description
              rowId
              sequence
              storageObject {
                blurhash
                bucket
                key
              }
            }
          }
        }
      }
    `,
    profileRef
  )
  const [_createProfilePhoto, isCreatingProfilePhoto] = useMutation<useProfilePhotosCreatePhotoMutation>(graphql`
    mutation useProfilePhotosCreatePhotoMutation($connections: [ID!]!, $input: CreatePhotoInput!) {
      createPhoto(input: $input) {
        photoEdge(orderBy: [SEQUENCE_ASC]) @appendEdge(connections: $connections) {
          cursor
          node {
            description
            id
            rowId
            sequence
            storageObject {
              bucket
              key
            }
          }
        }
      }
    }
  `)
  const createProfilePhoto = useCallback(
    (storageObjectId: string): Promise<useProfilePhotosCreatePhotoMutation['response']> =>
      new Promise((resolve, reject) =>
        _createProfilePhoto({
          onCompleted: (response, errors) => {
            if (errors) {
              reject(errors[0])
            } else {
              resolve(response)
            }
          },
          onError: (error) => reject(error),
          variables: {
            connections: profileData?.profilePhotos?.__id ? [profileData?.profilePhotos?.__id] : [],
            input: {
              photo: {
                storageObjectId,
                type: 'PROFILE'
              }
            }
          }
        })
      ),
    [_createProfilePhoto, profileData?.profilePhotos?.__id]
  )

  return {
    createProfilePhoto,
    isCreatingProfilePhoto,
    profilePhotosConnection: profileData?.profilePhotos,
    refetchProfilePhotos
  }
}
